import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, KpiCard, SectionTitle } from "@/components/Kpi";
import { inputCls } from "@/components/Filters";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, qs } from "@/lib/api";
import { eurShort, num } from "@/lib/format";

const GIORNI = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const pct = (v) => (v == null ? "n/d" : `${num(v, 1)}%`);
/** Valore di confronto con freccia: verde ▲ se l'agenzia nel periodo è sopra, rossa ▼ se sotto. */
const Conf = ({ ag, v }) => {
  const f = ag == null || v == null || ag === v ? null : ag > v ? "▲" : "▼";
  return <span>{pct(v)}{f && <span className={`ml-1 font-bold ${f === "▲" ? "text-emerald-600" : "text-red-600"}`} data-testid="freccia-confronto">{f}</span>}</span>;
};
const oggi = () => new Date().toLocaleDateString("sv-SE");

const Tabella = ({ testate, righe, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
        <tr>{testate.map((t) => <th key={t} className="px-3 py-2">{t}</th>)}</tr>
      </thead>
      <tbody>
        {righe.map((r, i) => (
          <tr key={i} className="border-t border-slate-100">
            {r.map((c, j) => <td key={j} className={`px-3 py-2 ${j ? "font-mono" : ""}`}>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PROGRAMMA_VUOTO = { giornaliero: { attivo: false, ora: "08:00" }, settimanale: { attivo: false, ora: "08:00", giorno: 0 }, agenzie: {}, direzione: [], tipi: {} };

/** Quali report riceve una riga (agenzia / DBE / DIREZIONE): assenza di scelta = entrambi. */
function TipiReport({ chiave, tipi, onChange, testId }) {
  const attivi = tipi?.[chiave] ?? ["giornaliero", "settimanale"];
  const toggle = (t) => onChange({ ...(tipi || {}), [chiave]: attivi.includes(t) ? attivi.filter((x) => x !== t) : [...attivi, t] });
  return (
    <div className="flex items-center gap-3 text-xs text-slate-600 mt-1" data-testid={testId}>
      {[["giornaliero", "Giornaliero"], ["settimanale", "Settimanale"]].map(([t, l]) => (
        <label key={t} className="flex items-center gap-1 cursor-pointer">
          <input type="checkbox" checked={attivi.includes(t)} onChange={() => toggle(t)} data-testid={`${testId}-${t}`} /> {l}
        </label>
      ))}
      {!attivi.length && <span className="text-amber-600">nessun invio</span>}
    </div>
  );
}
const emailValida = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

/** Elenco destinatari: chip rimovibili + aggiunta da utenti dell'app o indirizzo libero. */
function Destinatari({ valori, onChange, utenti, testId }) {
  const [libero, setLibero] = useState("");
  const aggiungi = (e) => {
    const v = (e || "").trim().toLowerCase();
    if (!v) return;
    if (!emailValida(v)) { toast.error(`Indirizzo non valido: ${v}`); return; }
    if (!valori.includes(v)) onChange([...valori, v]);
    setLibero("");
  };
  const disponibili = utenti.filter((u) => !valori.includes(u.email.toLowerCase()));
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      {valori.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 rounded-full bg-cyan-50 text-[#0E7490] text-xs px-2.5 py-1"
          data-testid={`${testId}-chip`}>
          {v}
          <button type="button" aria-label={`rimuovi ${v}`} className="hover:text-red-600" onClick={() => onChange(valori.filter((x) => x !== v))}>×</button>
        </span>
      ))}
      <select className={`${inputCls} w-44 text-xs`} value="" data-testid={`${testId}-utente`}
        onChange={(e) => aggiungi(e.target.value)}>
        <option value="">+ utente dell'app…</option>
        {disponibili.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
      </select>
      <input className={`${inputCls} w-56 text-xs`} placeholder="indirizzo email libero ↵" value={libero}
        data-testid={`${testId}-libero`} onChange={(e) => setLibero(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); aggiungi(libero); } }}
        onBlur={() => libero && aggiungi(libero)} />
    </div>
  );
}

/** Programmazione unica: orario giornaliero (feriali) e settimanale (giorno scelto), destinatari per agenzia e direzione. */
function ProgrammaInvii({ agenzie }) {
  const [p, setP] = useState(PROGRAMMA_VUOTO);
  const [utenti, setUtenti] = useState([]);
  const [ultimi, setUltimi] = useState([]);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => {
    api.get("/corto/report-agenzia/programma")
      .then((r) => { setP(r.data.programma); setUtenti(r.data.utenti || []); setUltimi(r.data.ultimi_invii || []); })
      .catch(() => {});
  }, []);
  const setTipo = (tipo, k, v) => setP((x) => ({ ...x, [tipo]: { ...x[tipo], [k]: v } }));
  const setAgenzia = (a, email) => setP((x) => ({ ...x, agenzie: { ...x.agenzie, [a]: email } }));
  const salva = async () => {
    setSalvando(true);
    try {
      const { data } = await api.put("/corto/report-agenzia/programma", { programma: p });
      setP(data.programma);
      toast.success("Programmazione salvata");
    } catch (e) { toast.error(apiError(e)); } finally { setSalvando(false); }
  };
  const Riga = ({ tipo, label, descr }) => (
    <div className="flex flex-wrap items-center gap-4 rounded-md border border-slate-200 p-3" data-testid={`programma-${tipo}`}>
      <label className="flex items-center gap-2 text-sm font-medium text-slate-800 w-44">
        <input type="checkbox" checked={!!p[tipo].attivo} data-testid={`programma-${tipo}-attivo`}
          onChange={(e) => setTipo(tipo, "attivo", e.target.checked)} /> {label}
      </label>
      <div className="flex items-center gap-2 text-sm text-slate-600">
        alle <input type="time" className={`${inputCls} w-28`} value={p[tipo].ora} data-testid={`programma-${tipo}-ora`}
          onChange={(e) => setTipo(tipo, "ora", e.target.value)} />
      </div>
      {tipo === "settimanale" && (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          ogni <select className={`${inputCls} w-36`} value={p.settimanale.giorno} data-testid="programma-settimanale-giorno"
            onChange={(e) => setTipo(tipo, "giorno", Number(e.target.value))}>
            {GIORNI.map((g, k) => <option key={g} value={k}>{g}</option>)}
          </select>
        </div>
      )}
      <span className="text-xs text-slate-500">{descr}</span>
    </div>
  );
  return (
    <Card className="p-6 space-y-5" data-testid="report-programma">
      <div>
        <div className="uppercase text-xs tracking-[0.18em] font-semibold text-[#0E7490]">Invio automatico via email</div>
        <p className="text-sm text-slate-500 mt-1">
          Ogni agenzia riceve solo il proprio report; la direzione riceve un'unica email con i report di tutte le agenzie.
          Per ogni riga puoi scegliere se inviare il giornaliero, il settimanale o entrambi.
          Chi vede questa pagina nell'app si decide con il permesso "Report agenzia" in Utenti.
        </p>
      </div>
      <div className="space-y-3">
        <Riga tipo="giornaliero" label="Report giornaliero" descr="tutte le mattine dei giorni feriali, con i dati del giorno prima" />
        <Riga tipo="settimanale" label="Report settimanale" descr="con i dati della settimana precedente (lunedì → venerdì)" />
      </div>
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Destinatari per agenzia</div>
        {agenzie.map((a) => (
          <div key={a.slug} className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-2 items-start border-t border-slate-100 pt-3"
            data-testid={`destinatari-${a.slug}`}>
            <div className="pt-1">
              <div className="text-sm font-medium text-slate-800">{a.agenzia}</div>
              {a.agenzia === "DBE" && <div className="text-xs text-slate-500">una sola email: report DBE + tutte le agenzie</div>}
              <TipiReport chiave={a.agenzia} tipi={p.tipi} testId={`tipi-${a.slug}`} onChange={(tipi) => setP((x) => ({ ...x, tipi }))} />
            </div>
            <Destinatari valori={p.agenzie[a.agenzia] || []} utenti={utenti} testId={`destinatari-${a.slug}-lista`}
              onChange={(v) => setAgenzia(a.agenzia, v)} />
          </div>
        ))}
        <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-2 items-start border-t-2 border-slate-200 pt-3"
          data-testid="destinatari-direzione">
          <div className="pt-1">
            <div className="text-sm font-semibold text-slate-900">DIREZIONE</div>
            <div className="text-xs text-slate-500">come DBE: una sola email con il report DBE e quello di ogni agenzia</div>
            <TipiReport chiave="DIREZIONE" tipi={p.tipi} testId="tipi-direzione" onChange={(tipi) => setP((x) => ({ ...x, tipi }))} />
          </div>
          <Destinatari valori={p.direzione || []} utenti={utenti} testId="destinatari-direzione-lista"
            onChange={(v) => setP((x) => ({ ...x, direzione: v }))} />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <button type="button" onClick={salva} disabled={salvando} data-testid="programma-salva"
          className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50">
          {salvando ? "Salvataggio…" : "Salva programmazione"}
        </button>
        {ultimi.length ? (
          <div className="text-xs text-slate-500" data-testid="programma-ultimi">
            Ultimi invii: {ultimi.slice(0, 5).map((u) => `${u.agenzia} ${u.tipo} ${u.dal} (${u.esiti.filter((e) => e.ok).length}/${u.esiti.length} ok)`).join(" · ")}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export default function ReportAgenzia() {
  const { puo, user } = useAuth();
  const [agenzie, setAgenzie] = useState([]);
  const [sel, setSel] = useState({ agenzia: "", tipo: "settimanale", data: oggi() });
  const [d, setD] = useState(null);
  const [invio, setInvio] = useState(false);

  useEffect(() => {
    api.get("/corto/agenzie").then((r) => {
      const consentite = user?.agenzie_report || [];
      const rows = [{ agenzia: "DBE", slug: "dbe" }, ...(r.data.rows || [])]
        .filter((a) => !consentite.length || consentite.includes(a.agenzia));
      setAgenzie(rows);
      setSel((s) => ({ ...s, agenzia: s.agenzia || rows[0]?.agenzia || "" }));
    }).catch(() => setAgenzie([]));
  }, []);
  useEffect(() => {
    if (!sel.agenzia) return;
    setD(null);
    api.get(`/corto/report-agenzia?${qs(sel)}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, [sel]);

  const inviaOra = async (aMe) => {
    setInvio(true);
    try {
      const { data } = await api.post("/corto/report-agenzia/invia", { ...sel, a_me: aMe });
      const ok = data.esiti.filter((e) => e.ok).length;
      toast.success(`Report inviato a ${ok}/${data.esiti.length} destinatari`);
    } catch (e) { toast.error(apiError(e)); } finally { setInvio(false); }
  };

  const v = d?.volumi; const b = d?.budget; const t = d?.telefonate; const p = d?.penetrazione;
  const periodo = d ? (d.dal === d.al ? d.dal : `${d.dal} → ${d.al}`) : "";
  const unita = d?.agenzie_dettaglio ? "Agenzia" : "Operatore";
  return (
    <div className="space-y-6" data-testid="report-agenzia-page">
      <SectionTitle sub="Volumi, erogato vs budget, telefonate e penetrazione assicurativa per agenzia: giornaliero (giorno prima) o settimanale (settimana precedente).">
        Report agenzia
      </SectionTitle>

      <Card className="p-4 flex flex-wrap items-end gap-4" data-testid="report-filtri">
        <div className="w-56">
          <label className="text-xs text-slate-500 block mb-1">Agenzia</label>
          <select className={inputCls} value={sel.agenzia} data-testid="report-agenzia-select"
            onChange={(e) => setSel({ ...sel, agenzia: e.target.value })}>
            {agenzie.map((a) => <option key={a.slug} value={a.agenzia}>{a.agenzia}</option>)}
          </select>
        </div>
        <div className="w-40">
          <label className="text-xs text-slate-500 block mb-1">Report</label>
          <select className={inputCls} value={sel.tipo} data-testid="report-tipo-select"
            onChange={(e) => setSel({ ...sel, tipo: e.target.value })}>
            <option value="giornaliero">Giornaliero</option>
            <option value="settimanale">Settimanale</option>
          </select>
        </div>
        <div className="w-44">
          <label className="text-xs text-slate-500 block mb-1">{sel.tipo === "settimanale" ? "Un giorno della settimana" : "Giorno"}</label>
          <input type="date" className={inputCls} value={sel.data} data-testid="report-data"
            onChange={(e) => setSel({ ...sel, data: e.target.value })} />
        </div>
        {d && (
          <a href={`/api/corto/report-agenzia.pdf?${qs(sel)}`} data-testid="report-scarica-pdf"
            className="px-4 py-2 rounded-md border border-[#0E7490] text-[#0E7490] text-sm hover:bg-cyan-50">Scarica PDF</a>
        )}
        {d && (
          <a href={`/api/corto/report-agenzia.html?${qs(sel)}`} target="_blank" rel="noreferrer" data-testid="report-apri-html"
            className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm hover:bg-slate-100">Apri versione email</a>
        )}
        {puo("corto_modifica_configurazioni") && (
          <>
            <button type="button" onClick={() => inviaOra(true)} disabled={invio || !d} data-testid="report-invia-a-me"
              className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm hover:bg-slate-100 disabled:opacity-50">
              {invio ? "Invio…" : "Inviami il report"}
            </button>
            <button type="button" onClick={() => inviaOra(false)} disabled={invio || !d} data-testid="report-invia-ora"
              className="px-4 py-2 rounded-md bg-[#0E7490] text-white text-sm hover:bg-[#0c637a] disabled:opacity-50">
              {invio ? "Invio…" : "Invia ai destinatari"}
            </button>
          </>
        )}
      </Card>

      {d ? (
        <>
          <div className="text-sm text-slate-500" data-testid="report-periodo">Periodo {periodo}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard label="Caricato" value={v.caricato.importo} kind="eur" accent="caricato"
              sub={`${num(v.caricato.pratiche)} pratiche`} testId="report-kpi-caricato" />
            <KpiCard label="Liquidato" value={v.liquidato.importo} kind="eur" accent="liquidato"
              sub={`${num(v.liquidato.pratiche)} pratiche`} testId="report-kpi-liquidato" />
            <KpiCard label="Target del periodo" value={b.target} kind="eur" accent="previsione"
              sub={`residuo quarter ${eurShort(b.residuo_quarter)} ÷ gg lavorativi rimanenti × ${b.giorni_lavorativi_periodo} gg`}
              testId="report-kpi-target" />
            <KpiCard label="Scostamento dal target" value={b.scostamento} kind="eur"
              accent={b.scostamento >= 0 ? "positivo" : "negativo"} sub={`${pct(b.raggiungimento_perc)} del target`}
              testId="report-kpi-scostamento" />
          </div>

          {d.agenzie_dettaglio && (
            <Card className="p-6" data-testid="report-agenzie-dettaglio">
              <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">Scostamento delle agenzie dal proprio target</div>
              <Tabella testId="report-agenzie-dettaglio-tabella" testate={["Agenzia", "Caricato", "Liquidato", "Target periodo", "Scostamento", "% target"]}
                righe={d.agenzie_dettaglio.map((r) => [r.agenzia, eurShort(r.caricato), eurShort(r.liquidato), eurShort(r.target),
                  <span className={`font-semibold ${r.scostamento >= 0 ? "text-emerald-600" : "text-red-600"}`}>{r.scostamento >= 0 ? "+" : ""}{eurShort(r.scostamento)}</span>,
                  pct(r.raggiungimento_perc)])} />
            </Card>
          )}

          {d.conto_carta && (
            <Card className="p-6" data-testid="report-conto-carta">
              <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">DB Conto Carta</div>
              <div className="text-xs text-slate-400 mb-3">dato mensile aggiornato settimanalmente · % = DB CC su pratiche liquidate nello stesso mese</div>
              <Tabella testId="report-conto-carta-tabella" testate={["Periodo", "DB CC", "Liquidate", `% ${d.agenzia}`, "% rete", "vs rete"]}
                righe={[d.conto_carta.mese, d.conto_carta.anno, ...d.conto_carta.mesi].map((r) => [
                  r.label, num(r.agenzia), num(r.liquidate), pct(r.perc), pct(r.perc_rete), <Conf ag={r.perc} v={r.perc_rete} />])} />
            </Card>
          )}

          <Card className="p-6">
            <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">Volumi per {unita}</div>
            <Tabella testId="report-volumi-operatore" testate={[unita, "Caricate", "Importo caricato", "Liquidate", "Importo liquidato"]}
              righe={(d.volumi_operatore || []).map((o) => [o.operatore, num(o.caricato), eurShort(o.caricato_importo), num(o.liquidato), eurShort(o.liquidato_importo)])} />
          </Card>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Card className="p-6">
              <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">
                Telefonate in uscita per {unita.toLowerCase()} · {num(t.totale_outbound)}
              </div>
              <Tabella testId="report-outbound" testate={[unita, "Chiamate", "Risposte", "% risposta"]}
                righe={t.outbound.map((o) => [o.operatore, num(o.chiamate), num(o.risposte), pct(o.perc_risposta)])} />
            </Card>
            <Card className="p-6">
              <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">Telefonate in ingresso</div>
              <div className="grid grid-cols-3 gap-4">
                <KpiCard label="Ricevute" value={t.inbound.totale} accent="sky" testId="report-inbound-totale" />
                <KpiCard label="Non risposte" value={t.inbound.non_risposte} accent="warning"
                  sub={pct(t.inbound.perc_non_risposte)} testId="report-inbound-non-risposte" />
                <KpiCard label="Non gestite" value={t.inbound.non_gestite} accent="negativo"
                  sub={`${pct(t.inbound.perc_non_gestite)} · senza richiamo in giornata`} testId="report-inbound-non-gestite" />
              </div>
            </Card>
          </div>

          <Card className="p-6">
            <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">
              Penetrazione assicurativa su pratiche liquidate
            </div>
            <Tabella testId="report-penetrazione" testate={["Perimetro", "CPI", "Bundle", "CPI + Bundle", "Liquidate"]}
              righe={[
                [`${d.agenzia} · periodo`, pct(p.agenzia.cpi), pct(p.agenzia.bundle), pct(p.agenzia.cpi_bundle), num(p.agenzia.pratiche)],
                ...[["Media DBE · stesso periodo", p.dbe], [`${d.agenzia} · da inizio anno`, p.agenzia_anno],
                  ...(p.settimana_precedente ? [["Settimana precedente", p.settimana_precedente]] : [])].map(([nome, r]) => [
                  nome, <Conf ag={p.agenzia.cpi} v={r.cpi} />, <Conf ag={p.agenzia.bundle} v={r.bundle} />,
                  <Conf ag={p.agenzia.cpi_bundle} v={r.cpi_bundle} />, num(r.pratiche)]),
              ]} />
          </Card>
        </>
      ) : <div className="text-sm text-slate-500">Caricamento…</div>}

      {puo("corto_modifica_configurazioni") && <ProgrammaInvii agenzie={agenzie} />}
    </div>
  );
}
